import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MARK_INVALID_BATCH, type BulkSelection, type EmailStatus, type MarkInvalidResult, type RemoveBadEmailsResult, type VerificationJobItem, type VerificationStatus, type VerifyResult, type VerifyVendor } from '@crm/shared';
import { api } from '../../api/client';

export const useVerificationStatus = () => useQuery({ queryKey: ['verification-status'], queryFn: async () => (await api.get<VerificationStatus>('/verification/status')).data });
export const useVerificationJobs = () =>
  useQuery({
    queryKey: ['verification-jobs'],
    queryFn: async () => (await api.get<VerificationJobItem[]>('/verification/jobs')).data,
    refetchInterval: (q) => (q.state.data?.some((j) => j.status === 'PENDING' || j.status === 'RUNNING') ? 3_000 : false),
  });

const KEYS = [['verification-status'], ['verification-jobs'], ['contacts'], ['contact'], ['activities'], ['segment-contacts'], ['list-contacts']];
function useInvalidate() {
  const qc = useQueryClient();
  return () => KEYS.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
}

export function useVerifyContact() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async ({ id, vendor }: { id: string; vendor?: VerifyVendor }) => (await api.post<VerifyResult>(`/verification/contacts/${id}`, { vendor: vendor ?? 'auto' })).data, onSuccess: inv });
}
export function useVerifySelection() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (input: { selection: BulkSelection; vendor?: VerifyVendor; onlyUnverified?: boolean; statuses?: EmailStatus[] }) => (await api.post<VerificationJobItem>('/verification/contacts', input)).data,
    onSuccess: inv,
  });
}

export function useStopVerificationJob() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (id: string) => (await api.post<VerificationJobItem>(`/verification/jobs/${id}/stop`)).data, onSuccess: inv });
}

export const STATUS_LABEL: Record<string, string> = { UNVERIFIED: 'Unverified', VALID: 'Valid', INVALID: 'Invalid', CATCH_ALL: 'Catch-all', DISPOSABLE: 'Disposable', UNKNOWN: 'Unknown', SUPPRESSED: 'Suppressed' };

export interface MarkInvalidProgress {
  done: number;
  total: number;
}

/** Sends the pasted list in batches; `apply: false` only reports what would change. Totals are summed across batches. */
/** Counts (apply: false) or removes bad addresses, calling the API batch by batch until none are left. */
export function useRemoveBadEmails() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async ({ statuses, apply, onProgress }: { statuses: EmailStatus[]; apply: boolean; onProgress?: (p: MarkInvalidProgress) => void }) => {
      const post = async () => (await api.post<RemoveBadEmailsResult>('/verification/remove-bad-emails', { statuses, apply })).data;
      let r = await post();
      if (!apply) return { matched: r.matched, removed: 0, failed: 0 };
      const total = r.matched;
      let removed = r.removed;
      let failed = r.failed;
      onProgress?.({ done: removed, total });
      // stop when a batch changes nothing, so contacts that keep failing can't loop forever
      while (r.remaining > 0 && r.removed > 0) {
        r = await post();
        removed += r.removed;
        failed = r.failed;
        onProgress?.({ done: removed, total });
      }
      return { matched: total, removed, failed };
    },
    onSuccess: (_, v) => {
      if (v.apply) inv();
    },
  });
}

export function useMarkInvalid() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async ({ emails, apply, block, onProgress }: { emails: string[]; apply: boolean; block?: boolean; onProgress?: (p: MarkInvalidProgress) => void }) => {
      const total: MarkInvalidResult = { marked: 0, alreadyInvalid: 0, suppressed: 0, secondary: [], notFound: [], blocked: 0 };
      for (let i = 0; i < emails.length; i += MARK_INVALID_BATCH) {
        const r = (await api.post<MarkInvalidResult>('/verification/mark-invalid', { emails: emails.slice(i, i + MARK_INVALID_BATCH), apply, block })).data;
        total.marked += r.marked;
        total.alreadyInvalid += r.alreadyInvalid;
        total.suppressed += r.suppressed;
        total.secondary.push(...r.secondary);
        total.notFound.push(...r.notFound);
        total.blocked += r.blocked;
        onProgress?.({ done: Math.min(emails.length, i + MARK_INVALID_BATCH), total: emails.length });
      }
      return total;
    },
    onSuccess: (_, v) => {
      if (v.apply) inv();
    },
  });
}
